import React, { useState } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Button,
  Link,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { replaceApplyFormData } from "./applyFormStorage";
import { saveUserSession } from "./userSessionStorage";

export default function UserSignInPage() {
  const navigate = useNavigate();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8001";

  const [values, setValues] = useState({
    email: "",
    password: "",
  });

  const [touched, setTouched] = useState({
    email: false,
    password: false,
  });

  const [focusedField, setFocusedField] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email);

  const errors = {
    email:
      touched.email && !values.email
        ? "Email is required"
        : touched.email && values.email && !emailIsValid
        ? "Enter a valid email address"
        : "",
    password: touched.password && !values.password ? "Password is required" : "",
  };

  const hasErrors = Object.values(errors).some(Boolean);

  const setAllTouched = () => {
    setTouched({
      email: true,
      password: true,
    });
  };

  const handleChange = (field) => (event) => {
    setValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleFocus = (field) => () => {
    setFocusedField(field);
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setFocusedField((prev) => (prev === field ? "" : prev));
  };

  const isFloating = (field) => focusedField === field || values[field] !== "";

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");

    setAllTouched();
    if (!values.email || !values.password || !emailIsValid || hasErrors) {
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await fetch(`${API_BASE_URL}/api/customer/login/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Client-App": "swiftcredit",
        },
        body: JSON.stringify({
          email: values.email,
          password: values.password,
        }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        setSubmitError(payload.message || "Unable to sign in right now. Please try again.");
        return;
      }

      const customer = payload.customer || {};
      const sessionAuth = {
        isAuthenticated: true,
        customerId: customer.id || null,
        email: customer.email || values.email,
      };

      replaceApplyFormData({
        auth: sessionAuth,
        preliminary: {
          loanAmount: customer.loanAmount || "",
          province: customer.province || "",
          email: sessionAuth.email || "",
          marketingConsent: Boolean(customer.marketingConsent),
          termsConsent: Boolean(customer.termsConsent),
        },
        customer: customer.personalInfo && typeof customer.personalInfo === "object" ? customer.personalInfo : {},
        income: customer.incomeInfo && typeof customer.incomeInfo === "object" ? customer.incomeInfo : {},
        bank: customer.bankInfo && typeof customer.bankInfo === "object" ? customer.bankInfo : {},
        flow: {},
      });

      saveUserSession({
        auth: sessionAuth,
        customer,
      });

      navigate("/profile");
    } catch {
      setSubmitError("Network error while signing in. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box width="100%" bg="gray.50" minH="100vh" pt={{ base: 28, md: 32 }} pb={{ base: 12, md: 16 }} px={{ base: 4, md: 8 }}>
      <VStack maxW="760px" mx="auto" spacing={8} align="stretch">
        <Box border="1px solid" borderColor="gray.200" borderRadius="2xl" bg="white" boxShadow="sm" px={{ base: 5, md: 8 }} py={{ base: 7, md: 9 }}>
          <VStack spacing={5} align="stretch">
            <VStack spacing={2} align="stretch" textAlign="center">
              <Heading size="lg">Sign in to your account</Heading>
              <Text color="gray.600" fontSize="sm">
                Sign in to view your profile and loan application details.
              </Text>
            </VStack>

            <form onSubmit={handleSubmit}>
              <VStack spacing={4} align="stretch">
                <FormControl isRequired isInvalid={Boolean(errors.email)} position="relative">
                  <FormLabel
                    position="absolute"
                    left="12px"
                    top={isFloating("email") ? "-9px" : "13px"}
                    zIndex={2}
                    px={2}
                    bg="white"
                    fontSize={isFloating("email") ? "xs" : "sm"}
                    color={isFloating("email") ? "gray.700" : "gray.500"}
                    pointerEvents="none"
                    transition="all 0.2s ease"
                    m={0}
                  >
                    Email
                  </FormLabel>
                  <Input
                    type="email"
                    value={values.email}
                    onChange={handleChange("email")}
                    onFocus={handleFocus("email")}
                    onBlur={handleBlur("email")}
                    bg="white"
                    h="52px"
                    borderRadius="lg"
                    borderColor="gray.300"
                    _focus={{ borderColor: "black", boxShadow: "none" }}
                    _hover={{ borderColor: "gray.400" }}
                    placeholder=""
                    required
                  />
                  <FormErrorMessage>{errors.email}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={Boolean(errors.password)} position="relative">
                  <FormLabel
                    position="absolute"
                    left="12px"
                    top={isFloating("password") ? "-9px" : "13px"}
                    zIndex={2}
                    px={2}
                    bg="white"
                    fontSize={isFloating("password") ? "xs" : "sm"}
                    color={isFloating("password") ? "gray.700" : "gray.500"}
                    pointerEvents="none"
                    transition="all 0.2s ease"
                    m={0}
                  >
                    Password
                  </FormLabel>
                  <InputGroup>
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={values.password}
                      onChange={handleChange("password")}
                      onFocus={handleFocus("password")}
                      onBlur={handleBlur("password")}
                      bg="white"
                      h="52px"
                      borderRadius="lg"
                      borderColor="gray.300"
                      _focus={{ borderColor: "black", boxShadow: "none" }}
                      _hover={{ borderColor: "gray.400" }}
                      placeholder=""
                      required
                    />
                    <InputRightElement h="52px">
                      <IconButton
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                        size="sm"
                        variant="ghost"
                        onClick={() => setShowPassword((prev) => !prev)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.password}</FormErrorMessage>
                </FormControl>

                <Text fontSize="sm" textAlign="right" mt={-1}>
                  <Link as={RouterLink} to="/reset-password" color="blue.500" textDecoration="underline" _hover={{ color: "blue.600" }}>
                    Forgot password?
                  </Link>
                </Text>

                {submitError && (
                  <Text color="red.500" fontSize="sm">
                    {submitError}
                  </Text>
                )}

                <Button type="submit" isLoading={isSubmitting} loadingText="Signing in" bg="black" color="white" borderRadius="full" h="50px" _hover={{ bg: "gray.900" }}>
                  Sign in
                </Button>

                <Text fontSize="sm" color="gray.700" textAlign="center">
                  Don&apos;t have an account?{" "}
                  <Link as={RouterLink} to="/apply" color="blue.500" textDecoration="underline" _hover={{ color: "blue.600" }}>
                    Start your application
                  </Link>
                </Text>
              </VStack>
            </form>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
